import { usePrevious } from '@gasbuddy/react-hooks';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useAnalytics from '../../../lib/hooks/useAnalytics';
import useProfile from '../../../lib/hooks/useProfile';
import useScreenView from '../../../lib/hooks/useScreenView';
import useVehicleMakes, { useVehicleMakeOptions } from '../../../lib/hooks/useVehicleMakes';
import useVehicleModels, { useVehicleModelOptions } from '../../../lib/hooks/useVehicleModels';
import { getReferrerHost } from '../../../lib/utils';
import { toQueryString } from '../../../lib/utils/querystring';
import { ANALYTICS_EVENTS } from '../../constants';
import Page from '../Page';
import VehicleRecallSearchBar from '../VehicleRecallSearchBar';
import VehicleRecallSearchPanel from '../VehicleRecallSearchPanel';

export default function VehicleRecallSearchPage() {
  const { isLoggedIn } = useProfile();
  const history = useHistory();
  const [year, setYear] = useState(undefined);
  const [makeId, setMakeId] = useState(undefined);
  const [modelId, setModelId] = useState(undefined);
  const [attemptedFetch, setAttemptedFetch] = useState(false);
  const analytics = useAnalytics();
  const previousModelId = usePrevious(modelId);
  const { data: makeData } = useVehicleMakes();
  const { data: modelData } = useVehicleModels(makeId, year);

  const makeOptions = useVehicleMakeOptions(makeData?.vehicleMakes);
  const modelOptions = useVehicleModelOptions(modelData?.vehicleModels);

  const { t } = useTranslation('recalls');

  const handleFieldChange = useCallback(({ target }) => {
    const updatedValue = Number(target.value);
    if (target.name === 'year') {
      setYear(updatedValue);
      setAttemptedFetch(false);
    } else if (target.name === 'makeId') {
      setMakeId(updatedValue);
      setAttemptedFetch(false);
    } else {
      setModelId(updatedValue);
    }
  }, []);

  const checkRecalls = useCallback(() => {
    const makeOption = makeOptions.find(option => option.value === makeId.toString());
    const modelOption = modelOptions.find(option => option.value === modelId.toString());

    const vehicleSearchParams = {
      year,
      make: makeOption.label,
      makeId,
      model: modelOption.label,
      modelId,
    };

    analytics.tagEvent({
      name: ANALYTICS_EVENTS.RECALL_SEARCH_CHECK_NOW_CLICKED,
      attributes: {
        ...vehicleSearchParams,
        logged_in: isLoggedIn,
      },
    });

    history.push(`/vehicles/recalls/results?${toQueryString(vehicleSearchParams)}`);
  }, [analytics, history, isLoggedIn, makeId, makeOptions, modelId, modelOptions, year]);

  useScreenView(ANALYTICS_EVENTS.RECALL_SEARCH_SCREEN_VIEWED, {
    // Log referrer domain to know about the source where user came from to this page
    referrer: getReferrerHost(),
    logged_in: isLoggedIn,
  }, [isLoggedIn]);

  // if selection of year or make changes, then re-fetch models
  useEffect(() => {
    if (year && makeId && !attemptedFetch) {
      setAttemptedFetch(true);
    }
  }, [year, makeId, attemptedFetch]);

  // if model options are available and currently selected model is no longer in the list then reset the model id to enforce selection
  useEffect(() => {
    if (modelOptions?.length && previousModelId) {
      const stillHasSameModel = !!modelOptions.find(option => option.value === previousModelId);
      if (!stillHasSameModel) {
        setModelId(0);
      }
    }
  }, [modelOptions, previousModelId]);

  return (
    <Page
      compact
      description={t('searchPageMetaDescription')}
      logScreenView={false}
      name="Vehicle Recalls"
      title={t('searchPageMetaTitle')}
    >
      <VehicleRecallSearchPanel>
        <VehicleRecallSearchBar
          year={year}
          makeId={makeId}
          modelId={modelId}
          makeOptions={makeOptions}
          modelOptions={modelOptions}
          onFieldChange={handleFieldChange}
          onCheckRecalls={checkRecalls}
        />
      </VehicleRecallSearchPanel>
    </Page>
  );
}
